/**
 * Раскладка вопроса клиента по колонкам доски.
 *
 * Правила колонки декларативные и живут в БД (редактируются в настройках):
 *  - keywords  — список слов/фраз целиком (например, гео-словарь для «Мира»);
 *  - include   — условия попадания; условие = any (хотя бы один) + all (все) + none (ни одного);
 *  - exclude   — условия, отменяющие попадание (например, «евро наличными» — не безнал);
 *  - yieldsToOthers + keep — колонка уступает другим сработавшим колонкам,
 *    если не сработал ни один keep-паттерн (Москва уступает Миру, если не сказано «Москва» явно).
 *  - amountMakesRequest — сообщение клиента без признаков вопроса, но с совпадением правил колонки и суммой,
 *    считается запросом («Ереван / Мой тез / Ваш синий 100к» — это запрос в «Мир», хоть и без «курс?»);
 *  - matchMakesRequest — то же, но сумма не нужна: совпадения с правилами хватает («Краснодар выдадим и» — запрос в «Мир»,
 *    сумму клиент часто пишет следующим сообщением).
 * Если не сработала ни одна колонка — вопрос уходит в колонку по умолчанию.
 * Одно сообщение может попасть в несколько колонок — тогда это несколько карточек.
 */
import { z } from 'zod';
import type { Classification } from './detection.js';
import { findHit, findKeywordHit, normalizeMessage, validatePattern } from './patterns.js';

const pattern = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, ctx) => {
    const err = validatePattern(value);
    if (err) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Невалидный паттерн: ${err}` });
  });

export const conditionSchema = z.object({
  any: z.array(pattern).default([]),
  all: z.array(pattern).default([]),
  none: z.array(pattern).default([]),
});
export type Condition = z.infer<typeof conditionSchema>;

export const columnRulesSchema = z.object({
  keywords: z.array(z.string().trim().min(1)).default([]),
  include: z.array(conditionSchema).default([]),
  exclude: z.array(conditionSchema).default([]),
  yieldsToOthers: z.boolean().default(false),
  keep: z.array(pattern).default([]),
  amountMakesRequest: z.boolean().default(false),
  matchMakesRequest: z.boolean().default(false),
  /**
   * Кто закрывает карточку колонки ответом в чате (id сотрудников). Пусто — как раньше (по колонкам сотрудника).
   * Если задано — ответы остальных колонка игнорирует: карточку не закрывают и в статистику по людям не идут.
   * Доступ к колонке на доске это не меняет.
   */
  closerEmployeeIds: z.array(z.number().int().positive()).default([]),
});
export type ColumnRules = z.infer<typeof columnRulesSchema>;

export interface RoutableColumn {
  key: string;
  enabled: boolean;
  isDefault: boolean;
  sort: number;
  rules: ColumnRules;
}

export interface ConditionHit {
  any: string | null;
  all: string[];
}

export interface ColumnDecision {
  key: string;
  reason: 'keyword' | 'rule' | 'default' | 'attachment' | 'manual' | 'pinned';
  hits: string[];
}

export interface RoutingTrace {
  matched: ColumnDecision[];
  excluded: { key: string; by: string[] }[];
  yielded: string[];
  result: ColumnDecision[];
}

function evalCondition(cond: Condition, text: string): string[] | null {
  if (!cond.any.length && !cond.all.length) return null;
  const hits: string[] = [];
  if (cond.any.length) {
    const hit = findHit(cond.any, text);
    if (!hit) return null;
    hits.push(hit.match);
  }
  for (const p of cond.all) {
    const hit = findHit([p], text);
    if (!hit) return null;
    hits.push(hit.match);
  }
  if (cond.none.length && findHit(cond.none, text)) return null;
  return hits;
}

export function routeText(text: string, columns: readonly RoutableColumn[]): RoutingTrace {
  const active = columns.filter((c) => c.enabled).sort((a, b) => a.sort - b.sort);
  const trace: RoutingTrace = { matched: [], excluded: [], yielded: [], result: [] };

  for (const col of active) {
    const kw = findKeywordHit(col.rules.keywords, text)?.match ?? null;
    let decision: ColumnDecision | null = kw ? { key: col.key, reason: 'keyword', hits: [kw] } : null;
    if (!decision) {
      for (const cond of col.rules.include) {
        const hits = evalCondition(cond, text);
        if (hits) {
          decision = { key: col.key, reason: 'rule', hits };
          break;
        }
      }
    }
    if (!decision) continue;

    const excludedBy = col.rules.exclude.map((c) => evalCondition(c, text)).find((h) => h !== null);
    if (excludedBy) {
      trace.excluded.push({ key: col.key, by: excludedBy });
      continue;
    }
    trace.matched.push(decision);
  }

  const byKey = new Map(active.map((c) => [c.key, c]));
  let result = trace.matched.filter((d) => {
    const col = byKey.get(d.key)!;
    if (!col.rules.yieldsToOthers) return true;
    const othersMatched = trace.matched.some((o) => o.key !== d.key && !byKey.get(o.key)!.rules.yieldsToOthers);
    if (!othersMatched) return true;
    if (col.rules.keep.length && findHit(col.rules.keep, text)) return true;
    trace.yielded.push(d.key);
    return false;
  });

  if (!result.length) {
    const def = active.find((c) => c.isDefault) ?? active[0];
    if (def) result = [{ key: def.key, reason: 'default', hits: [] }];
  }
  trace.result = result;
  return trace;
}

/**
 * Сумма в сообщении: «100к», «1.5кк», «2 млн», «1 764 600», «500’000», «300000». Голое число — от 5 цифр (чтобы год не считался суммой).
 * Разделитель тысяч — пробел (в т.ч. неразрывный и узкий) или апостроф (' ’ ` ´), как пишут с iPhone и в швейцарском формате,
 * а также запятая или точка («30,000€», «1.500.000»): число с ведущей цифрой 1–9, группы ровно по 3 цифры и дальше не продолжается
 * цифрами — «0,550», «87,75», «1,5кк» и даты «12.10.2026» сюда не попадают.
 */
const AMOUNT_RE = /\d+(?:[.,]\d+)?\s*(?:кк|к|млн|тыс|k|m)(?![\p{L}])|\d{1,3}(?:[ \u00a0\u202f\u2009'\u2019`\u00b4]\d{3})+|(?<![\d.,])[1-9]\d{0,2}(?:([.,])\d{3})(?:\1\d{3})*(?![\d.,]*\d)|\d{5,}/iu;
export const hasAmount = (text: string) => AMOUNT_RE.test(normalizeMessage(text));

/**
 * Сообщение клиента, не распознанное как вопрос, становится запросом, если сработали правила колонки
 * с флагом matchMakesRequest или с флагом amountMakesRequest и суммой в тексте.
 * Возвращает исходную классификацию, если правило не сработало.
 */
export function applyAmountRequest(cls: Classification, text: string | null, columns: readonly RoutableColumn[]): Classification {
  // короткие тоже: «Атланта», «Рига 50к» — короче минимальной длины, но город колонки однозначно говорит о запросе
  if (cls.kind !== 'client_message' || (cls.reason !== 'no_question' && cls.reason !== 'too_short') || !text?.trim()) return cls;
  const amount = hasAmount(text);
  const flagged = columns.filter((c) => c.enabled && (c.rules.matchMakesRequest || (amount && c.rules.amountMakesRequest)));
  if (!flagged.length) return cls;
  const hit = routeText(text, columns).result.find((d) => flagged.some((c) => c.key === d.key) && d.reason !== 'default');
  if (!hit) return cls;
  const byAmount = amount && flagged.find((c) => c.key === hit.key)!.rules.amountMakesRequest;
  return { kind: 'client_question', group: byAmount ? 'column_amount' : 'column_match', hit: hit.hits[0] ?? '', attachment: false };
}
